export enum AppMode {
  FULL_RENOVATION = 'FULL_RENOVATION',
  STAGING_ONLY = 'STAGING_ONLY'
}


/**
 * BUG-134 — what the user wants CHANGED, sent to the API as `changeIntent`.
 *
 * `AppMode` is a pipeline flag (run the renovate pass or not) and only has two
 * values, so it cannot express the four jobs Sam actually sells. The API has
 * carried these four intents since BUG-131; this is the UI half.
 *
 * The API keeps accepting the legacy AppMode values, so old clients are safe.
 */
export enum ChangeIntent {
  /** Empty room -> fully furnished in a style. */
  FURNISH = 'furnish',
  /** Keep every furniture piece; restyle soft goods and decor only. */
  REFRESH = 'refresh',
  /** Replace the loose furniture; keep built-ins, floor and wall colour. */
  SWAP = 'swap',
  /** Change everything except the architecture. */
  RESTYLE = 'restyle',
}

/** Step 1 of the picker: is there furniture in the photo? */
export enum RoomState {
  EMPTY = 'EMPTY',
  FURNISHED = 'FURNISHED',
}

/**
 * Which intents to offer per room state, in display order.
 *
 * Sam's rule (2026-09-06): a makelaar never sees more than 3 buttons. Asking
 * the room-state question first is what keeps it to 3 instead of 5.
 */
export const INTENTS_BY_ROOM_STATE: Record<RoomState, ChangeIntent[]> = {
  [RoomState.EMPTY]: [ChangeIntent.FURNISH, ChangeIntent.RESTYLE],
  [RoomState.FURNISHED]: [
    // BUG-135 — ChangeIntent.REFRESH is deliberately NOT offered yet.
    //
    // The intent, its prompt and its region budget all exist and work. The
    // MODEL does not deliver it: measured live on prod (render 325, Gemini
    // 3.1 Flash image-edit, scandinavian), a refresh request changed 61.6% of
    // the furniture region where the promise allows at most 45%. It quietly
    // replaces the furniture instead of restyling around it, and the region
    // gate correctly rejects that — after the user has waited.
    //
    // Shipping a button whose promise the model cannot keep is worse than not
    // shipping it: the user pays a credit and gets "verwerking mislukt".
    // Re-enable once the furniture mask anchors the sofa hard (freeze the
    // masked region instead of asking the model to leave it alone) and a live
    // render lands inside the refresh budget. See REFRESH_BLOCKED_REASON.
    ChangeIntent.SWAP,
    ChangeIntent.RESTYLE,
  ],
};

/**
 * BUG-135 — why REFRESH is absent from the picker, in one place so the reason
 * cannot drift from the code. Asserted by changeIntentPicker.test.ts, which
 * fails if REFRESH is re-added to INTENTS_BY_ROOM_STATE while this constant
 * still names it as blocked.
 */
export const REFRESH_BLOCKED_REASON =
  'Model haalt de belofte niet: 2 van 15 corpusrenders binnen budget, mediaan 48,6% verandering waar maximaal 32% mag, in elk kamertype (60-render corpus, 2026-09-06, hermeten na de alpha-maskerfix). Wacht op een wis-en-vul-pass.';

export const INTENT_LABELS: Record<ChangeIntent, string> = {
  [ChangeIntent.FURNISH]: 'Inrichten',
  [ChangeIntent.REFRESH]: 'Opknappen',
  [ChangeIntent.SWAP]: 'Meubels vervangen',
  [ChangeIntent.RESTYLE]: 'Volledig restylen',
};

/**
 * Descriptions double as the contract the render is judged against: the API's
 * region budget enforces exactly what these sentences promise. Changing a
 * sentence here without changing the budget is a defect — there is a test for
 * that alignment in stagery-api (uiPromiseAlignment.test.js).
 */
export const INTENT_DESCRIPTIONS: Record<ChangeIntent, string> = {
  [ChangeIntent.FURNISH]:
    'Voor een lege ruimte. Plaatst een volledige inrichting in de gekozen stijl.',
  [ChangeIntent.REFRESH]:
    'Houdt je meubels precies zoals ze staan. Alleen kussens, kleden, planten en decoratie worden vernieuwd.',
  [ChangeIntent.SWAP]:
    'Nieuwe meubels in de gekozen stijl. Keuken, kasten, vloer en wandkleur blijven precies zoals ze zijn.',
  [ChangeIntent.RESTYLE]:
    'Vernieuwt ook het timmerwerk, de vloer en de wandkleuren, en richt de ruimte daarna opnieuw in.',
};

export const ROOM_STATE_LABELS: Record<RoomState, string> = {
  [RoomState.EMPTY]: 'Kaal opgeleverd',
  [RoomState.FURNISHED]: 'Volledig ingericht',
};

export const ROOM_STATE_DESCRIPTIONS: Record<RoomState, string> = {
  [RoomState.EMPTY]: 'Geen of nauwelijks meubels op de foto',
  [RoomState.FURNISHED]: 'Er staan meubels in de ruimte',
};

/**
 * The renovate pass repaints joinery, floor and wall colour. Only two intents
 * are allowed to do that, and it must match `intentAllowsRenovation` in the
 * API — the frontend value is what pins the pipeline mode on the event.
 */
export function intentToAppMode(intent: ChangeIntent): AppMode {
  return intent === ChangeIntent.RESTYLE || intent === ChangeIntent.FURNISH
    ? AppMode.FULL_RENOVATION
    : AppMode.STAGING_ONLY;
}

export enum RoomType {
  LIVING_ROOM = 'Living Room',
  BEDROOM = 'Bedroom',
  KITCHEN = 'Kitchen',
  BATHROOM = 'Bathroom',
  OFFICE = 'Office'
}

export enum StagingStyle {
  MODERN = 'modern',
  CLASSIC = 'classic',
  SCANDINAVIAN = 'scandinavian',
  JAPANDI = 'japandi',
  EUROPEAN = 'european_classic',
  LUXURY = 'luxury',
}

export const STYLE_LABELS: Record<StagingStyle, string> = {
  [StagingStyle.MODERN]: 'Modern',
  [StagingStyle.CLASSIC]: 'Klassiek',
  [StagingStyle.SCANDINAVIAN]: 'Scandinavisch',
  [StagingStyle.JAPANDI]: 'Japandi',
  [StagingStyle.EUROPEAN]: 'Europees Klassiek',
  [StagingStyle.LUXURY]: 'Luxe',
};

export const STYLE_DESCRIPTIONS: Record<StagingStyle, string> = {
  [StagingStyle.MODERN]: 'Strakke lijnen, neutrale kleuren, lichte materialen',
  [StagingStyle.CLASSIC]: 'Warme houtsoorten, traditionele meubels, tapijten',
  [StagingStyle.SCANDINAVIAN]: 'Licht hout, minimalistisch, veel wit en natuur',
  [StagingStyle.JAPANDI]: 'Japans x Scandinavisch, organische vormen, aardse tinten',
  [StagingStyle.EUROPEAN]: 'Elegante lijnen, zachte stoffen, verfijnde accenten',
  [StagingStyle.LUXURY]: 'Marmer, donkere accenten, goud details, premium materialen',
};

export const ROOM_LABELS: Record<string, string> = {
  [RoomType.LIVING_ROOM]: 'Woonkamer',
  [RoomType.BEDROOM]: 'Slaapkamer',
  [RoomType.KITCHEN]: 'Keuken',
  [RoomType.BATHROOM]: 'Badkamer',
  [RoomType.OFFICE]: 'Kantoor',
};

export interface ProcessingState {
  isProcessing: boolean;
  step: 'idle' | 'uploading' | 'renovating' | 'staging' | 'complete';
  progress: number; // 0 to 100
  message: string;
}

export interface ImageAsset {
  original: string | null;
  renovated: string | null; // Step A result (Clean Base)
  staged: string | null;    // Step B result (Final)
}

export interface UserProfile {
  firstName: string;
  lastName?: string | null;
  companyName: string;
  email: string;
  credits: number;
  referralCode: string;
  role: 'beheerder' | 'medewerker' | 'superadmin';
  avatarUrl?: string | null;
  companyLogoUrl?: string | null;
  websiteUrl?: string | null;
  // A/B experiment
  abVariant?: string | null;
  abExperiment?: string | null;
  trialActive?: boolean;
  trialEndsAt?: string | null;
  utmSource?: string | null;
  utmMedium?: string | null;
  utmCampaign?: string | null;
  utmContent?: string | null;
}

export interface TeamMember {
  id: number;
  email: string;
  first_name: string;
  company_name: string;
  role: 'beheerder' | 'medewerker' | 'superadmin';
  is_active: number;
  credits: number;
  last_login: string | null;
  property_count?: number;
  render_count?: number;
  last_render_at?: string | null;
}

/**
 * Shape returned by GET /api/team/stats (getTeamUsageStats in lib/db.js).
 * Fields mirror the SQL SELECT: id, email, first_name, role, credits,
 * last_login, renders_total, renders_last_30d, last_render_at.
 */
export interface TeamStatRow {
  id: number;
  email: string;
  first_name: string | null;
  role: 'beheerder' | 'medewerker' | 'superadmin';
  credits: number;
  last_login: string | null;
  renders_total: number;
  renders_last_30d: number;
  last_render_at: string | null;
}

export interface VariantState {
  id: number;
  original: string | null;
  renovated: string | null;
  staged: string | null;
  status: 'pending' | 'uploading' | 'renovating' | 'staging' | 'done' | 'error';
  progress: number;
  error: string | null;
  // STA-185: pipeline jobId, set once the variant transitions to 'done'.
  // Passed to saveRender() on variant pick so the backend dedupes against
  // the pipeline-persisted render row.
  jobId?: string | null;
}